import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { isUnauthenticated } from "@empite/api-client";
import { adminClient } from "./boot";
import { adminKeys } from "./admin-keys";
import type {
  AddedProviderAccount,
  Alert,
  AlertDetail,
  AlertFilter,
  AlertKind,
  AlertThresholdEdit,
  AlertThresholdEnvelope,
  CloneMarkupPlanBody,
  ClonePackageBody,
  TenantBillingOverview,
  TenantNotification,
  TenantPlanCancelled,
  TenantPlanChanged,
  TenantUpgradeCheckout,
  ApplicationEntitlement,
  AudienceTier,
  AdminSession,
  AnalyticsApplications,
  AnalyticsApplicationsQuery,
  AnalyticsOverview,
  AnalyticsRevenue,
  AnalyticsTopUsers,
  AnalyticsTopUsersQuery,
  AnalyticsUserDetail,
  AnalyticsWindowQuery,
  AppMetadataFilter,
  AppMetadataPage,
  AuditAction,
  AuditFilter,
  FailureAcknowledgement,
  FailureDetail,
  FailurePage,
  FailureQuery,
  FailureSummary,
  ApplicationDetail,
  ApplicationEdit,
  ApplicationStatusResult,
  BindingRevokeResult,
  CustomerSubscription,
  DeviceRestoreResult,
  DeviceRevokeResult,
  DeviceSignOutResult,
  EnabledStatus,
  EntitlementRevokeResult,
  OAuthClient,
  OAuthClientEdit,
  OAuthClientStatusResult,
  Reason,
  ReasonVocabulary,
  SecurityEvent,
  SecurityEventFilter,
  SessionRevokeResult,
  SignInBody,
  SignUpBody,
  SignUpResult,
  TeamInvitation,
  TenantAPIKey,
  TenantAPIKeyCreated,
  NewTenantAPIKey,
  NewInvitation,
  InvitationCreated,
  AcceptInviteBody,
  Operator,
  UsageDriftQuery,
  UsageDriftReport,
  UserDetail,
  UserDeveloperFlagResult,
  UserFilter,
  UserStatusResult,
  ApplicationSummary,
  UserSummary,
  MarkupPlan,
  MarkupPlanChange,
  MarkupPlanEdit,
  MarkupPlanFilter,
  MarkupPlanStatusChange,
  ModelDeprecationAdvice,
  ModelPolicy,
  ModelPolicyEdit,
  DiscoveredModel,
  ModelAssignment,
  ModelAudienceResult,
  ModelStatusResult,
  ModelTestResult,
  ModelTestRun,
  ModelUsageFilter,
  ModelUsageReport,
  NewApplication,
  NewMarkupPlan,
  NewModelPolicy,
  NewOAuthClient,
  NewProviderModel,
  NewPackage,
  NewPackageVersion,
  NewPriceVersion,
  NewProfileProvider,
  NewProviderAccount,
  NewProviderTemplate,
  NewRoutingProfile,
  Package,
  PackageArchive,
  PackageDetail,
  PackageFilter,
  PackageMigrationReport,
  PackageVersionChange,
  PriceProposal,
  PriceProposalFilter,
  PriceProposalPublished,
  PublishPriceProposal,
  PriceVersion,
  PriceVersionFilter,
  PriceVersionStatusChange,
  PriceVersionTransition,
  ProviderAccountDetail,
  ProviderAccountStatus,
  ProviderAccountStatusResult,
  ProviderAccountSummary,
  ProviderCredential,
  ProviderModel,
  ProviderModelEdit,
  ProviderModelStatus,
  ProviderTemplate,
  ProviderTemplateEdit,
  ProviderTemplateStatus,
  RedirectURIReplacement,
  TenantRosterRow,
  TenantTier,
  TenantTierEdit,
  RoutingProfileDetail,
  RoutingProfileEdit,
  RoutingProfileStatusResult,
  RoutingProfileSummary,
  LedgerTransaction,
  Reservation,
  ReservationFilter,
  SettlementFailure,
  TopUp,
  TopUpFilter,
  Wallet,
  WalletDetail,
  WalletFilter,
} from "./admin-client";

/**
 * The admin portal's data layer.
 *
 * Deliberately NOT @empite/session: those hooks are the customer SSO
 * surface, they call /v1, and they are authenticated by a cookie this realm
 * does not have. The style is copied from them on purpose — same query-key
 * discipline, same "signed out is data, not an error" rule — because two
 * portals behaving differently about staleness and invalidation is how one
 * of them ends up showing a revoked device as active.
 *
 * Hand-written alongside src/admin-client.ts and for the same reason: the
 * admin surface has no OpenAPI contract to generate from yet.
 */

// --------------------------------------------------------------------------
// Who is signed in
// --------------------------------------------------------------------------

/**
 * useAdminSession answers "is an operator signed in", as DATA.
 *
 * GET /admin/v1/session answers 401 when nobody is, which is correct HTTP and
 * a terrible React query: every screen would render an error state on the
 * ordinary path of a signed-out visitor. The 401 is folded into null here,
 * once, so the guard downstream sees "no session" rather than "something
 * broke".
 */
export function useAdminSession(): UseQueryResult<AdminSession | null> {
  return useQuery({
    queryKey: adminKeys.session,
    queryFn: async () => {
      try {
        return await adminClient.session();
      } catch (err) {
        if (isUnauthenticated(err)) {
          return null;
        }
        throw err;
      }
    },
    retry: false,
    staleTime: 30_000,
  });
}

/** True only once the session has loaded, so a guard cannot flash the
 *  sign-in screen at an operator who is already signed in. */
export function useSignedInOperator(): {
  ready: boolean;
  signedIn: boolean;
  session: AdminSession | null;
} {
  const query = useAdminSession();
  return {
    ready: !query.isPending,
    signedIn: Boolean(query.data),
    session: query.data ?? null,
  };
}

export function useAdminSignIn(): UseMutationResult<AdminSession, unknown, SignInBody> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (body: SignInBody) => adminClient.signIn(body),
    onSuccess: (session) => {
      // Everything already cached was read as somebody else — or as nobody.
      // Dropping the whole realm is what stops the previous operator's user
      // list being handed to the next one.
      queries.clear();
      // Then put the ONE answer back that the shell cannot draw itself
      // without.
      //
      // clear() removes the queries, it does not refetch them, and an
      // invalidate afterwards has an empty cache to work on — so the nav's
      // useAdminSession observer went on rendering the last thing it saw,
      // which for somebody who just signed in is "signed out". Every link is
      // gated on the operator's permissions, so the whole bar stayed as the
      // Sign in link until a full page load rebuilt the cache. Only the
      // `?next=` path escaped it, because that one navigates with
      // window.location and reloads by definition.
      //
      // Seeding rather than invalidating, because POST /session already
      // answered with the session document GET /session would return: a
      // refetch here would ask the same question twice and leave the shell
      // signed-out for the width of the round trip.
      queries.setQueryData(adminKeys.session, session);
    },
  });
}

export function useAdminSignOut(): UseMutationResult<void, unknown, void> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: () => adminClient.signOut(),
    // clear(), not invalidate(): an invalidated cache still re-renders its old
    // rows while the refetch is in flight, which shows a signed-out browser
    // the customer records it just left.
    onSettled: () => {
      queries.clear();
    },
  });
}

// --------------------------------------------------------------------------
// Self-signup, invitation acceptance, and the tenant's own team
// --------------------------------------------------------------------------

/**
 * useAdminSignUp provisions a tenant for somebody who has no session.
 *
 * It clears the cache on success for the reason useAdminSignIn does — anything
 * already read was read as nobody — even though this route sets no cookie. The
 * screen signs the new owner in afterwards through the ordinary sign-in path,
 * which is where the session actually comes from.
 */
export function useAdminSignUp(): UseMutationResult<SignUpResult, unknown, SignUpBody> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (body: SignUpBody) => adminClient.signUp(body),
    onSuccess: () => {
      queries.clear();
    },
  });
}

/** useAcceptInvitation turns a link into an operator account. No session comes
 *  back; the new operator signs in like everybody else. */
export function useAcceptInvitation(): UseMutationResult<
  { operator: Operator },
  unknown,
  AcceptInviteBody
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (body: AcceptInviteBody) => adminClient.acceptInvitation(body),
    onSuccess: () => {
      queries.clear();
    },
  });
}

export function useTeamMembers(): UseQueryResult<{ members: Operator[] }> {
  return useQuery({
    queryKey: adminKeys.teamMembers,
    queryFn: () => adminClient.listTeamMembers(),
  });
}

export function useTeamInvitations(): UseQueryResult<{ invitations: TeamInvitation[] }> {
  return useQuery({
    queryKey: adminKeys.teamInvites,
    queryFn: () => adminClient.listInvitations(),
  });
}

export interface CreateInvitationVariables {
  body: NewInvitation;
  reason: Reason;
}

/**
 * Offer a seat. Invalidates the WHOLE team prefix rather than the invitation
 * list alone: re-inviting an address whose earlier offer expired leaves two
 * rows in different states, and the member list is the other half of the
 * answer the screen is drawing.
 */
export function useCreateInvitation(): UseMutationResult<
  InvitationCreated,
  unknown,
  CreateInvitationVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: CreateInvitationVariables) =>
      adminClient.createInvitation(vars.body, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.team });
    },
  });
}

export interface RevokeInvitationVariables {
  inviteID: string;
  reason: Reason;
}

export function useRevokeInvitation(): UseMutationResult<
  { invitation: TeamInvitation },
  unknown,
  RevokeInvitationVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: RevokeInvitationVariables) =>
      adminClient.revokeInvitation(vars.inviteID, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.team });
    },
  });
}

// The tenant's API keys — see screens/api-keys.tsx.

export function useTenantAPIKeys(tenantID?: string): UseQueryResult<{ api_keys: TenantAPIKey[] }> {
  return useQuery({
    queryKey: adminKeys.tenantAPIKeys(tenantID ?? ""),
    queryFn: () => adminClient.listTenantAPIKeys(tenantID),
  });
}

export interface CreateTenantAPIKeyVariables {
  body: NewTenantAPIKey;
  reason: Reason;
}

export function useCreateTenantAPIKey(): UseMutationResult<
  TenantAPIKeyCreated,
  unknown,
  CreateTenantAPIKeyVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: CreateTenantAPIKeyVariables) =>
      adminClient.createTenantAPIKey(vars.body, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.tenantAPIKeysAll });
    },
  });
}

export interface RevokeTenantAPIKeyVariables {
  keyID: string;
  reason: Reason;
  tenantID?: string;
}

export function useRevokeTenantAPIKey(): UseMutationResult<
  { api_key: TenantAPIKey },
  unknown,
  RevokeTenantAPIKeyVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: RevokeTenantAPIKeyVariables) =>
      adminClient.revokeTenantAPIKey(vars.keyID, vars.reason, vars.tenantID),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.tenantAPIKeysAll });
    },
  });
}

/**
 * useReasonVocabulary loads the codes the reason prompt offers.
 *
 * It is fetched rather than compiled in because the server owns the list and
 * rejects anything outside it: a portal shipping its own copy would offer an
 * operator a code that fails at submit. Cached for the session — the
 * vocabulary changes with a deployment, not with a page.
 */
export function useReasonVocabulary(): UseQueryResult<ReasonVocabulary> {
  return useQuery({
    queryKey: adminKeys.reasonCodes,
    queryFn: () => adminClient.reasonCodes(),
    staleTime: 10 * 60_000,
  });
}

// --------------------------------------------------------------------------
// Applications
// --------------------------------------------------------------------------

export function useApplications(): UseQueryResult<{ applications: ApplicationSummary[] }> {
  return useQuery({
    queryKey: adminKeys.applications,
    queryFn: () => adminClient.listApplications(),
  });
}

export function useApplication(applicationID: string): UseQueryResult<ApplicationDetail> {
  return useQuery({
    queryKey: adminKeys.application(applicationID),
    queryFn: () => adminClient.getApplication(applicationID),
  });
}

export interface CreateApplicationVariables {
  body: NewApplication;
  reason: Reason;
}

/**
 * Register an application.
 *
 * The whole detail document comes back and is SEEDED into the cache under the
 * new id, because the operator is about to be navigated onto exactly that page:
 * without it the create is followed by a spinner over data the console was
 * already handed. The list is invalidated rather than appended to — it carries
 * counts this response cannot know it changed.
 */
export function useCreateApplication(): UseMutationResult<
  ApplicationDetail,
  unknown,
  CreateApplicationVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: CreateApplicationVariables) =>
      adminClient.createApplication(vars.body, vars.reason),
    onSuccess: async (result) => {
      queries.setQueryData(adminKeys.application(result.application.id), result);
      await queries.invalidateQueries({ queryKey: adminKeys.applications });
    },
  });
}

export interface UpdateApplicationVariables {
  applicationID: string;
  body: ApplicationEdit;
  reason: Reason;
}

export function useUpdateApplication(): UseMutationResult<
  ApplicationDetail,
  unknown,
  UpdateApplicationVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: UpdateApplicationVariables) =>
      adminClient.updateApplication(vars.applicationID, vars.body, vars.reason),
    // The list carries the name, so refreshing only the page in front of the
    // operator would leave the row they came from contradicting it.
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.application(vars.applicationID) });
      await queries.invalidateQueries({ queryKey: adminKeys.applications });
    },
  });
}

export interface CreateOAuthClientVariables {
  applicationID: string;
  body: NewOAuthClient;
  reason: Reason;
}

/** The create answers with just the client — the operator is already on the
 *  application page — so there is nothing to merge and the page is refetched.
 *  The list is invalidated too: `oauth_client_count` moved. */
export function useCreateOAuthClient(): UseMutationResult<
  { oauth_client: OAuthClient },
  unknown,
  CreateOAuthClientVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: CreateOAuthClientVariables) =>
      adminClient.createOAuthClient(vars.applicationID, vars.body, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.application(vars.applicationID) });
      await queries.invalidateQueries({ queryKey: adminKeys.applications });
    },
  });
}

export interface UpdateOAuthClientVariables {
  clientID: string;
  body: OAuthClientEdit;
  reason: Reason;
  /** Not sent — the route is addressed by client id alone. Carried so the
   *  application page it was edited on can be refreshed afterwards. */
  applicationID: string;
}

export function useUpdateOAuthClient(): UseMutationResult<
  { oauth_client: OAuthClient },
  unknown,
  UpdateOAuthClientVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: UpdateOAuthClientVariables) =>
      adminClient.updateOAuthClient(vars.clientID, vars.body, vars.reason),
    // No count on the list changes when a client is renamed or re-scoped, so
    // only the page is refreshed.
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.application(vars.applicationID) });
    },
  });
}

export interface ReplaceRedirectURIsVariables {
  clientID: string;
  /** The WHOLE list, already trimmed the way the server trims it. There is no
   *  add/remove variant on purpose: a delta applied by two operators in two
   *  tabs would let the loser's removal silently reappear. */
  redirectURIs: string[];
  reason: Reason;
  /** Not sent — the route is addressed by client id alone. Carried so the
   *  application page it was edited on can be refreshed afterwards. */
  applicationID: string;
}

/**
 * Replace a client's callback list.
 *
 * Only the application page is invalidated: the applications LIST carries a
 * client count, and replacing a list never changes it — a client that lost
 * every callback would have been refused, not deleted.
 */
export function useReplaceRedirectURIs(): UseMutationResult<
  RedirectURIReplacement,
  unknown,
  ReplaceRedirectURIsVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: ReplaceRedirectURIsVariables) =>
      adminClient.replaceRedirectURIs(vars.clientID, vars.redirectURIs, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.application(vars.applicationID) });
    },
  });
}

export interface ApplicationStatusVariables {
  applicationID: string;
  status: EnabledStatus;
  reason: Reason;
}

export function useSetApplicationStatus(): UseMutationResult<
  ApplicationStatusResult,
  unknown,
  ApplicationStatusVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: ApplicationStatusVariables) =>
      adminClient.setApplicationStatus(vars.applicationID, vars.status, vars.reason),
    // The list carries the status too, so refreshing only the page in front of
    // the operator would leave the row they came from contradicting it.
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.application(vars.applicationID) });
      await queries.invalidateQueries({ queryKey: adminKeys.applications });
    },
  });
}

export interface OAuthClientStatusVariables {
  clientID: string;
  status: EnabledStatus;
  reason: Reason;
  /** Not sent — the route is addressed by client id alone. Carried so the
   *  application page it was clicked on can be refreshed afterwards. */
  applicationID: string;
}

export function useSetOAuthClientStatus(): UseMutationResult<
  OAuthClientStatusResult,
  unknown,
  OAuthClientStatusVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: OAuthClientStatusVariables) =>
      adminClient.setOAuthClientStatus(vars.clientID, vars.status, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.application(vars.applicationID) });
    },
  });
}

export interface EntitlementRevokeVariables {
  entitlementID: string;
  reason: Reason;
  applicationID: string;
}

export function useRevokeEntitlement(): UseMutationResult<
  EntitlementRevokeResult,
  unknown,
  EntitlementRevokeVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: EntitlementRevokeVariables) =>
      adminClient.revokeEntitlement(vars.entitlementID, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.application(vars.applicationID) });
      // A revoked entitlement changes the count on the list as well.
      await queries.invalidateQueries({ queryKey: adminKeys.applications });
    },
  });
}

// --------------------------------------------------------------------------
// Users, devices, bindings, sessions
// --------------------------------------------------------------------------

/**
 * useUsers is the search.
 *
 * The filter is part of the query KEY as well as of the request: two
 * different searches are two different questions, and sharing one cache entry
 * between them shows whichever answer landed last under whichever term is in
 * the box.
 */
export function useUsers(filter: UserFilter): UseQueryResult<{ users: UserSummary[] }> {
  return useQuery({
    queryKey: adminKeys.userList(filter),
    queryFn: () => adminClient.listUsers(filter),
  });
}

export function useUser(userID: string): UseQueryResult<UserDetail> {
  return useQuery({
    queryKey: adminKeys.user(userID),
    queryFn: () => adminClient.getUser(userID),
  });
}

/**
 * A user's subscriptions, fetched separately from the account.
 *
 * Its own query because it is behind its own permission — `packages:read`,
 * not `users:read` — so a 403 here has to be renderable as one card's notice
 * instead of failing the whole support page. Retry is left at the default:
 * the card reads `isApiError(err).status === 403` and says so in words.
 */
export function useUserSubscriptions(
  userID: string,
  enabled = true,
): UseQueryResult<{ subscriptions: CustomerSubscription[] }> {
  return useQuery({
    queryKey: adminKeys.userSubscriptions(userID),
    queryFn: () => adminClient.listUserSubscriptions(userID),
    // `enabled` is how the caller says "this operator does not hold
    // packages:read". Spending a round trip to be told 403 by a server that
    // already told this console its permissions at sign-in puts a refusal in
    // the network log of a portal that is behaving correctly.
    enabled,
  });
}

/**
 * One account's authentication and device trail.
 *
 * Behind `users:read` — the same permission the account read above needs — so
 * `enabled` is not really about a role that lacks it (such an operator never
 * gets past the profile card). It is here because this route AUDITS ITSELF:
 * reading personal data writes an admin_actions row with the filter attached,
 * and a query fired for a permission the console knows it does not hold would
 * fill the trail with denials nobody made.
 *
 * `placeholderData` keeps the previous page on screen while the next one is
 * fetched. The filter and the page size are both in the query key, so without
 * it every "show more" would replace the table with a spinner and lose the
 * operator's place in the middle of reading an incident.
 */
export function useUserSecurityEvents(
  userID: string,
  filter: SecurityEventFilter,
  enabled = true,
): UseQueryResult<{ security_events: SecurityEvent[] }> {
  return useQuery({
    queryKey: adminKeys.userSecurityEvents(userID, filter),
    queryFn: () => adminClient.listUserSecurityEvents(userID, filter),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/**
 * What one account's installations have reported about themselves.
 *
 * Behind `users:read`, and AUDITED — reading a customer's machine inventory
 * writes an admin_actions row with the filter attached — so `enabled` is here
 * for useUserSecurityEvents' reason: a query fired for a permission the console
 * knows it does not hold would fill the trail with denials nobody made.
 *
 * `placeholderData` keeps the previous page on screen while the next one is
 * fetched, so growing the page or picking a source does not replace the table
 * with a spinner.
 */
export function useUserAppMetadata(
  userID: string,
  filter: AppMetadataFilter,
  enabled = true,
): UseQueryResult<AppMetadataPage> {
  return useQuery({
    queryKey: adminKeys.userAppMetadata(userID, filter),
    queryFn: () => adminClient.userAppMetadata(userID, filter),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/**
 * The same table read one INSTALLATION at a time, and the only place a whole
 * payload can be asked for.
 *
 * Behind `devices:read`, which is a DIFFERENT permission from the account list
 * above — billing_ops holds one and not the other — so a screen that shows both
 * has to gate them separately rather than on one grant.
 *
 * `filter.full` is part of the query key, so turning it on refetches instead of
 * repainting the elided page it already has.
 */
export function useDeviceAppMetadata(
  deviceID: string,
  filter: AppMetadataFilter,
  enabled = true,
): UseQueryResult<AppMetadataPage> {
  return useQuery({
    queryKey: adminKeys.deviceAppMetadata(deviceID, filter),
    queryFn: () => adminClient.deviceAppMetadata(deviceID, filter),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export interface UserVariables {
  userID: string;
  reason: Reason;
}

/**
 * Suspending and reactivating both land on the same cache, so they share one
 * invalidation: `adminKeys.users` covers the detail page AND every search
 * result the operator may still have open behind it.
 */
export function useSuspendUser(): UseMutationResult<UserStatusResult, unknown, UserVariables> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: UserVariables) => adminClient.suspendUser(vars.userID, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
}

export function useReactivateUser(): UseMutationResult<UserStatusResult, unknown, UserVariables> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: UserVariables) => adminClient.reactivateUser(vars.userID, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
}

export interface DeveloperFlagVariables extends UserVariables {
  /** The state the operator wants, not a flip. See setUserDeveloperFlag. */
  isEmpiteDeveloper: boolean;
}

/**
 * The Empite-developer marker.
 *
 * Invalidates the whole `users` prefix like the two above it rather than just
 * this user's detail key: the flag is a column in the search results as well
 * as a row on this page, and a list left behind unrefreshed contradicts the
 * badge the operator is looking at — worse, the developer FILTER on that
 * screen would keep returning an account that no longer matches it.
 */
export function useSetUserDeveloperFlag(): UseMutationResult<
  UserDeveloperFlagResult,
  unknown,
  DeveloperFlagVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: DeveloperFlagVariables) =>
      adminClient.setUserDeveloperFlag(vars.userID, vars.isEmpiteDeveloper, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
}

/**
 * The three revocations under a user.
 *
 * Each carries `userID` even though the route does not need it: the server
 * addresses the device, binding or session by its own id, and the portal
 * needs to know whose page to refresh. Revoking a device also ends its
 * bindings and sessions server-side, so the whole user subtree is invalidated
 * rather than the one table the button lives in.
 */
export interface DeviceRevokeVariables {
  deviceID: string;
  reason: Reason;
  userID: string;
}

/**
 * useSignOutDevice ends whoever is signed in on an installation and leaves the
 * installation working — the everyday support action, undone by the person
 * simply signing in again on the same machine.
 *
 * Its own hook rather than a flag on useRevokeDevice: the two are different
 * routes writing different revocation reasons, and only one of them is
 * reversible. A shared mutation with a boolean is one mis-set boolean away from
 * bricking a customer's laptop.
 */
export function useSignOutDevice(): UseMutationResult<
  DeviceSignOutResult,
  unknown,
  DeviceRevokeVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: DeviceRevokeVariables) =>
      adminClient.signOutDevice(vars.deviceID, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.user(vars.userID) });
    },
  });
}

export function useRevokeDevice(): UseMutationResult<
  DeviceRevokeResult,
  unknown,
  DeviceRevokeVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: DeviceRevokeVariables) => adminClient.revokeDevice(vars.deviceID, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.user(vars.userID) });
    },
  });
}

/**
 * useRestoreDevice reopens an installation its owner retired.
 *
 * A third hook rather than a flag, for the reason the second one exists: it is
 * a third route, and the server refuses it for anything an operator blocked.
 * Nothing here decides that — the button is offered from `revoked_reason` and
 * the answer is enforced server-side, so a portal bug cannot unblock a laptop.
 */
export function useRestoreDevice(): UseMutationResult<
  DeviceRestoreResult,
  unknown,
  DeviceRevokeVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: DeviceRevokeVariables) =>
      adminClient.restoreDevice(vars.deviceID, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.user(vars.userID) });
    },
  });
}

export interface BindingRevokeVariables {
  bindingID: string;
  reason: Reason;
  userID: string;
}

export function useRevokeBinding(): UseMutationResult<
  BindingRevokeResult,
  unknown,
  BindingRevokeVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: BindingRevokeVariables) =>
      adminClient.revokeBinding(vars.bindingID, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.user(vars.userID) });
    },
  });
}

export interface SessionRevokeVariables {
  sessionID: string;
  reason: Reason;
  userID: string;
}

export function useRevokeSession(): UseMutationResult<
  SessionRevokeResult,
  unknown,
  SessionRevokeVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: SessionRevokeVariables) =>
      adminClient.revokeSession(vars.sessionID, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.user(vars.userID) });
    },
  });
}

// --------------------------------------------------------------------------
// Provider accounts and credentials
// --------------------------------------------------------------------------

export function useProviderAccounts(): UseQueryResult<{
  provider_accounts: ProviderAccountSummary[];
}> {
  return useQuery({
    queryKey: adminKeys.providers,
    queryFn: () => adminClient.listProviderAccounts(),
  });
}

/**
 * The provider catalogue.
 *
 * One hook for both audiences, because there is one route: platform staff get
 * every status, a tenant operator gets the published rows whatever it asks
 * for. `status` is part of the key rather than a client-side filter so the
 * super admin's console and the operator's picker never read each other's
 * cached answer.
 */
/**
 * The operator tier price list — every row, drafts and archived included.
 *
 * Platform-only. `tenants:manage` is in the Admin API's platform-only set, so a
 * tenant-confined operator gets a 403 and an audited denial from this call
 * whatever roles they hold; the Tiers screen is nav-gated on the same grant so
 * they are never offered it.
 */
export function useTenantTiers(): UseQueryResult<{ tiers: TenantTier[] }> {
  return useQuery({
    queryKey: adminKeys.tenantTiers,
    queryFn: () => adminClient.listTenantTiers(),
  });
}

export interface TenantTierEditVariables {
  tierID: string;
  body: TenantTierEdit;
  reason: Reason;
}

/**
 * A tier edit.
 *
 * It invalidates the ROSTER as well as the price list, and only because the
 * roster renders each tenant's tier name and price beside its consumption —
 * renaming or re-pricing a tier changes what that column says. The usage
 * numbers themselves do not move: what a subscriber is spending against was
 * copied onto its period when the period opened.
 */
export function useUpdateTenantTier(): UseMutationResult<
  { tier: TenantTier },
  unknown,
  TenantTierEditVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: TenantTierEditVariables) =>
      adminClient.updateTenantTier(vars.tierID, vars.body, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.tenantTiers });
      await queries.invalidateQueries({ queryKey: adminKeys.tenantRoster });
    },
  });
}

/** Every company, its tier and its consumption. Gated on `tenants:read_all`. */
export function useTenantRoster(): UseQueryResult<{ tenants: TenantRosterRow[] }> {
  return useQuery({
    queryKey: adminKeys.tenantRoster,
    queryFn: () => adminClient.listTenantRoster(),
  });
}

/** One company. Gated on `tenants:read_all`, like the roster it is a row of. */
export function useTenant(tenantID: string): UseQueryResult<{ tenant: TenantRosterRow }> {
  return useQuery({
    queryKey: adminKeys.tenant(tenantID),
    queryFn: () => adminClient.getTenant(tenantID),
  });
}

export interface TenantPaymentsHandledExternallyVariables {
  tenantID: string;
  enabled: boolean;
  reason: Reason;
}

/**
 * Turn the external-payments flag on or off. The answer is the updated row, so
 * the company page is written from it directly; the roster prefix (which also
 * covers every company page) is invalidated because the list carries the flag.
 */
export function useSetTenantPaymentsHandledExternally(): UseMutationResult<
  { tenant: TenantRosterRow },
  unknown,
  TenantPaymentsHandledExternallyVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: TenantPaymentsHandledExternallyVariables) =>
      adminClient.setTenantPaymentsHandledExternally(vars.tenantID, vars.enabled, vars.reason),
    onSuccess: async (result, vars) => {
      queries.setQueryData(adminKeys.tenant(vars.tenantID), result);
      await queries.invalidateQueries({ queryKey: adminKeys.tenantRoster });
    },
  });
}

/**
 * The CALLER's own bill — the tier, the consumption and the offers.
 *
 * `tenant_billing:read`, which both tenant seats hold: knowing the allowance is
 * nearly gone is what makes it possible to ask the owner to upgrade. Platform
 * staff hold it through no role and are told so in a sentence rather than shown
 * an empty page — there is no "my company" for a session that belongs to none.
 */
export function useTenantBilling(): UseQueryResult<TenantBillingOverview> {
  return useQuery({
    queryKey: adminKeys.tenantBilling,
    queryFn: () => adminClient.tenantBilling(),
  });
}

export interface TenantUpgradeVariables {
  planSlug: string;
  reason: Reason;
}

/**
 * Open a tier checkout.
 *
 * IT INVALIDATES NOTHING, and that is the point: a 201 here is a URL to pay at,
 * not a tier that moved. The allowance changes when the provider's signed
 * webhook arrives, and refetching the overview now would redraw the same tier
 * and invite the operator to read the redraw as a confirmation.
 */
export function useStartTenantUpgrade(): UseMutationResult<
  TenantUpgradeCheckout,
  unknown,
  TenantUpgradeVariables
> {
  return useMutation({
    mutationFn: (vars: TenantUpgradeVariables) =>
      adminClient.startTenantUpgrade(vars.planSlug, vars.reason),
  });
}

/**
 * Stop the company's tier renewing. Invalidates the whole tenant-billing
 * prefix: the overview's cancel_at_period_end and next_renewal both move.
 */
export function useCancelTenantPlan(): UseMutationResult<TenantPlanCancelled, unknown, Reason> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (reason: Reason) => adminClient.cancelTenantPlan(reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.tenantBilling });
    },
  });
}

/**
 * Change the company's recurring tier. Unlike an upgrade checkout this DOES
 * invalidate: the provider subscription was re-priced by the call itself, and
 * the overview — not the answer's `plan` — says whether the tier has moved.
 */
export function useChangeTenantPlan(): UseMutationResult<
  TenantPlanChanged,
  unknown,
  TenantUpgradeVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: TenantUpgradeVariables) =>
      adminClient.changeTenantPlan(vars.planSlug, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.tenantBilling });
    },
  });
}

/**
 * The company's billing notices. `unread` is the banner's read. `enabled` lets
 * the layout skip the call for a session that has no tenant or no
 * `tenant_billing:read`, which would otherwise be an audited denial per page.
 */
export function useTenantNotifications(
  unread: boolean,
  enabled = true,
): UseQueryResult<{ notifications: TenantNotification[] }> {
  return useQuery({
    queryKey: adminKeys.tenantNotificationsFor(unread),
    queryFn: () => adminClient.listTenantNotifications({ unread }),
    enabled,
  });
}

export interface MarkTenantNotificationReadVariables {
  notificationID: string;
  reason: Reason;
}

/** Dismiss one notice for the company; refreshes both notice lists. */
export function useMarkTenantNotificationRead(): UseMutationResult<
  { notification: TenantNotification },
  unknown,
  MarkTenantNotificationReadVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: MarkTenantNotificationReadVariables) =>
      adminClient.markTenantNotificationRead(vars.notificationID, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.tenantNotifications });
    },
  });
}

export interface ClonePriceVersionVariables {
  versionID: string;
  reason: Reason;
}

/**
 * Take a copy of an inherited platform rate.
 *
 * The whole `pricing` prefix, for the reason the create does: the clone closes
 * the tenant's own predecessor for that (provider, model, currency) at the same
 * instant, so the row the operator was looking at is not the only cached list
 * that has changed underneath them.
 */
export function useClonePriceVersion(): UseMutationResult<
  { price_version: PriceVersion },
  unknown,
  ClonePriceVersionVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: ClonePriceVersionVariables) =>
      adminClient.clonePriceVersion(vars.versionID, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.pricing });
    },
  });
}

export interface CloneMarkupPlanVariables {
  planID: string;
  body: CloneMarkupPlanBody;
  reason: Reason;
}

export function useCloneMarkupPlan(): UseMutationResult<
  { markup_plan: MarkupPlan },
  unknown,
  CloneMarkupPlanVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: CloneMarkupPlanVariables) =>
      adminClient.cloneMarkupPlan(vars.planID, vars.body, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.pricing });
    },
  });
}

export interface ClonePackageVariables {
  packageID: string;
  body: ClonePackageBody;
  reason: Reason;
}

/** The copy lands as a DRAFT under one of the caller's own applications, so it
 *  belongs in the unfiltered list, in that application's list and in the draft
 *  list — the whole `packages` prefix, as the create does. */
export function useClonePackage(): UseMutationResult<
  { package: Package },
  unknown,
  ClonePackageVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: ClonePackageVariables) =>
      adminClient.clonePackage(vars.packageID, vars.body, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.packages });
    },
  });
}

export function useProviderTemplates(
  status?: ProviderTemplateStatus,
): UseQueryResult<{ provider_templates: ProviderTemplate[] }> {
  return useQuery({
    queryKey: status
      ? adminKeys.providerTemplatesFor(status)
      : adminKeys.providerTemplates,
    queryFn: () => adminClient.listProviderTemplates({ status }),
  });
}

export interface NewProviderTemplateVariables {
  body: NewProviderTemplate;
  reason: Reason;
}

/** Publishing a definition. The prefix covers the filtered lists too: a new
 *  template appears in "everything" and, if it is born published, in the
 *  picker beside it. */
export function useCreateProviderTemplate(): UseMutationResult<
  { provider_template: ProviderTemplate },
  unknown,
  NewProviderTemplateVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: NewProviderTemplateVariables) =>
      adminClient.createProviderTemplate(vars.body, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.providerTemplates });
    },
  });
}

export interface ProviderTemplateEditVariables {
  templateID: string;
  body: ProviderTemplateEdit;
  reason: Reason;
}

export function useUpdateProviderTemplate(): UseMutationResult<
  { provider_template: ProviderTemplate },
  unknown,
  ProviderTemplateEditVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: ProviderTemplateEditVariables) =>
      adminClient.updateProviderTemplate(vars.templateID, vars.body, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.providerTemplates });
    },
  });
}

export interface ProviderTemplateStatusVariables {
  templateID: string;
  status: ProviderTemplateStatus;
  reason: Reason;
}

/** Retire, publish or send back to draft. It moves no tenant account — the
 *  ones already instantiated keep serving traffic — so only the catalogue is
 *  invalidated. */
export function useSetProviderTemplateStatus(): UseMutationResult<
  { provider_template: ProviderTemplate },
  unknown,
  ProviderTemplateStatusVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: ProviderTemplateStatusVariables) =>
      adminClient.setProviderTemplateStatus(vars.templateID, vars.status, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.providerTemplates });
    },
  });
}

/**
 * The add-provider variables carry a plaintext upstream key, and are the only
 * variables in this portal besides a rotation that do.
 *
 * TanStack keeps a mutation's variables after the call settles, so the screen
 * calls `reset()` and clears the field's own state the moment the account is
 * written — the same discipline useRotateProviderCredential documents, for the
 * same reason.
 */
export interface NewProviderAccountVariables {
  body: NewProviderAccount;
  reason: Reason;
}

export function useAddProviderAccount(): UseMutationResult<
  AddedProviderAccount,
  unknown,
  NewProviderAccountVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: NewProviderAccountVariables) =>
      adminClient.addProviderAccount(vars.body, vars.reason),
    // The accounts prefix only. A tenant instantiating a template changes
    // nothing about the template it was made from.
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.providers });
    },
  });
}

export function useProviderAccount(accountID: string): UseQueryResult<ProviderAccountDetail> {
  return useQuery({
    queryKey: adminKeys.provider(accountID),
    queryFn: () => adminClient.getProviderAccount(accountID),
  });
}

export interface ProviderAccountStatusVariables {
  accountID: string;
  status: ProviderAccountStatus;
  reason: Reason;
}

export function useSetProviderAccountStatus(): UseMutationResult<
  ProviderAccountStatusResult,
  unknown,
  ProviderAccountStatusVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: ProviderAccountStatusVariables) =>
      adminClient.setProviderAccountStatus(vars.accountID, vars.status, vars.reason),
    // The prefix covers the list and every account page under it: suspending
    // an account changes the row the operator navigated from as well.
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.providers });
    },
  });
}

/**
 * The rotate variables carry a plaintext upstream key, and are the only
 * variables in this portal that do.
 *
 * TanStack keeps a mutation's variables in its cache after the call settles,
 * which is fine for an id and wrong for a secret — so the screen calls
 * `reset()` the moment the rotation succeeds, and the secret's own React
 * state is cleared in the same breath. `accountID` is not sent anywhere: the
 * route addresses the credential by its own id, and this is only how the hook
 * knows which account page to refresh.
 */
export interface CredentialRotateVariables {
  credentialID: string;
  secret: string;
  reason: Reason;
  accountID: string;
}

export function useRotateProviderCredential(): UseMutationResult<
  { credential: ProviderCredential },
  unknown,
  CredentialRotateVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: CredentialRotateVariables) =>
      adminClient.rotateProviderCredential(vars.credentialID, vars.secret, vars.reason),
    // Only the account detail: a rotation changes the fingerprint, the last
    // four and the timestamps on one card, and none of the numbers on the
    // list beside it.
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.provider(vars.accountID) });
    },
  });
}

export interface CredentialRevokeVariables {
  credentialID: string;
  reason: Reason;
  accountID: string;
}

export function useRevokeProviderCredential(): UseMutationResult<
  { credential: ProviderCredential },
  unknown,
  CredentialRevokeVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: CredentialRevokeVariables) =>
      adminClient.revokeProviderCredential(vars.credentialID, vars.reason),
    // A revoke moves the active credential count, which the list column
    // shows, so this one does go wider than the page.
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.providers });
    },
  });
}

// --------------------------------------------------------------------------
// Models
// --------------------------------------------------------------------------

/**
 * The whole catalogue.
 *
 * `enabled` defaults to true so every existing call site is unchanged. It
 * exists for the reason `useRoutingProfiles`' does: the Admin API's Guard
 * audits every refusal, so a read issued for a permission the operator does not
 * hold writes a denial nobody chose to attempt.
 */
export function useProviderModels(enabled = true): UseQueryResult<{ models: ProviderModel[] }> {
  return useQuery({
    queryKey: adminKeys.models,
    queryFn: () => adminClient.listProviderModels(),
    enabled,
  });
}

/**
 * The catalogue narrowed to what one application routes to.
 *
 * A separate hook rather than a parameter on the one above, because the two
 * have different cache entries and different call sites: the models page reads
 * the whole catalogue and filters in the header, while an application's own
 * screen only ever wants its own rows and should not pull five hundred to find
 * six.
 *
 * `enabled` so a screen can hold it until it knows which application it is on.
 */
export function useProviderModelsFor(
  applicationID: string | undefined,
): UseQueryResult<{ models: ProviderModel[] }> {
  return useQuery({
    queryKey: adminKeys.modelsFor(applicationID ?? ""),
    queryFn: () => adminClient.listProviderModels({ application_id: applicationID }),
    enabled: applicationID !== undefined && applicationID !== "",
  });
}

export interface NewProviderModelVariables {
  body: NewProviderModel;
  reason: Reason;
}

/**
 * Add a catalogue row.
 *
 * Invalidates the whole model list rather than pushing the returned row into
 * the cache: the server decides `status`, `created_at` and what the family
 * column's NULLIF made of a blank, and a hand-merged row would show the
 * operator their own submission wearing the server's clothes.
 */
export function useCreateProviderModel(): UseMutationResult<
  { model: ProviderModel },
  unknown,
  NewProviderModelVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: NewProviderModelVariables) =>
      adminClient.createProviderModel(vars.body, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.models });
    },
  });
}

export interface ProviderModelEditVariables {
  modelID: string;
  body: ProviderModelEdit;
  reason: Reason;
}

/** Correct a catalogue row. PATCH semantics live in the body the screen
 *  builds — see ProviderModelEdit for what omitting a key means as against
 *  sending an explicit null. */
export function useUpdateProviderModel(): UseMutationResult<
  { model: ProviderModel },
  unknown,
  ProviderModelEditVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: ProviderModelEditVariables) =>
      adminClient.updateProviderModel(vars.modelID, vars.body, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.models });
    },
  });
}

export interface ModelStatusVariables {
  modelID: string;
  status: ProviderModelStatus;
  reason: Reason;
  /** Only for a move to deprecated or retired; the server answers 400 if it
   *  travels with `available`. */
  advice?: ModelDeprecationAdvice;
}

/**
 * What one provider account offers, fetched on demand.
 *
 * `enabled` because this is the ONE query in the console that makes the server
 * call a third party: it must fire when an operator opens the picker and asks
 * for an account, never on a page load. It is also deliberately outside the
 * `models` prefix — no write this console makes can change what a vendor
 * offers, so a deprecation must not re-fetch a vendor catalogue.
 */
export function useAvailableModels(
  accountID: string | undefined,
): UseQueryResult<{ provider_account_id: string; models: DiscoveredModel[] }> {
  return useQuery({
    queryKey: adminKeys.availableModels(accountID ?? ""),
    queryFn: () => adminClient.availableModels(accountID ?? ""),
    enabled: accountID !== undefined && accountID !== "",
    // A vendor catalogue changes when a vendor ships, not while somebody is
    // filling in a form. Five minutes keeps a picker reopened twice in a row
    // from making two upstream calls.
    staleTime: 300_000,
  });
}

export interface ModelTestVariables {
  modelID: string;
  run: ModelTestRun;
  reason: Reason;
}

/**
 * Run one prompt against one model.
 *
 * It invalidates NOTHING. A test changes no row this console reads — no
 * catalogue status, no policy, no price — so a refetch afterwards would be
 * network for its own sake, and refetching the models list would throw away the
 * result the operator is reading.
 */
export function useTestProviderModel(): UseMutationResult<
  { result: ModelTestResult },
  unknown,
  ModelTestVariables
> {
  return useMutation({
    mutationFn: (vars: ModelTestVariables) =>
      adminClient.testProviderModel(vars.modelID, vars.run, vars.reason),
  });
}

export interface AssignModelsVariables {
  profileID: string;
  modelIDs: string[];
  reason: Reason;
}

/**
 * Tick catalogue models onto an application.
 *
 * Invalidates the PROFILE — where the policies live — and the model list,
 * whose "Used by" column is now one application longer.
 */
export function useAssignModels(): UseMutationResult<
  { routing_profile_id: string; assignments: ModelAssignment[] },
  unknown,
  AssignModelsVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: AssignModelsVariables) =>
      adminClient.assignModels(vars.profileID, vars.modelIDs, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.routingProfile(vars.profileID) });
      await queries.invalidateQueries({ queryKey: adminKeys.models });
    },
  });
}

export interface UnassignModelVariables {
  profileID: string;
  modelID: string;
  reason: Reason;
}

export function useUnassignModel(): UseMutationResult<void, unknown, UnassignModelVariables> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: UnassignModelVariables) =>
      adminClient.unassignModel(vars.profileID, vars.modelID, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.routingProfile(vars.profileID) });
      await queries.invalidateQueries({ queryKey: adminKeys.models });
    },
  });
}

export interface ModelAliasStatusVariables {
  profileID: string;
  alias: string;
  status: EnabledStatus;
  reason: Reason;
}

/** Turn one model off, or back on, for one application. */
export function useSetModelAliasStatus(): UseMutationResult<
  { model: string; status: EnabledStatus; routes_changed: number },
  unknown,
  ModelAliasStatusVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: ModelAliasStatusVariables) =>
      adminClient.setModelAliasStatus(vars.profileID, vars.alias, vars.status, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.routingProfile(vars.profileID) });
    },
  });
}

export interface ModelAudienceVariables {
  profileID: string;
  alias: string;
  audience: AudienceTier;
  reason: Reason;
}

/**
 * Release one alias, within one profile, to one ring.
 *
 * Invalidates the PROFILE rather than the model list: the audience lives on the
 * routing profile document, and the catalogue rows are untouched by it. It also
 * invalidates the model list's application-scoped entries, because an operator
 * who has just held a model back to the team is very likely looking at that
 * application's screen when they do it.
 */
export function useSetModelAudience(): UseMutationResult<
  ModelAudienceResult,
  unknown,
  ModelAudienceVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: ModelAudienceVariables) =>
      adminClient.setModelAudience(vars.profileID, vars.alias, vars.audience, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.routingProfile(vars.profileID) });
      await queries.invalidateQueries({ queryKey: adminKeys.models });
    },
  });
}

export interface EntitlementTierVariables {
  entitlementID: string;
  applicationID: string;
  tier: AudienceTier;
  reason: Reason;
}

/** Move one subject between release rings for one application. */
export function useSetEntitlementTier(): UseMutationResult<
  { entitlement: ApplicationEntitlement },
  unknown,
  EntitlementTierVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: EntitlementTierVariables) =>
      adminClient.setEntitlementTier(vars.entitlementID, vars.tier, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.application(vars.applicationID) });
    },
  });
}

export function useSetProviderModelStatus(): UseMutationResult<
  ModelStatusResult,
  unknown,
  ModelStatusVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: ModelStatusVariables) =>
      adminClient.setProviderModelStatus(vars.modelID, vars.status, vars.reason, vars.advice),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.models });
    },
  });
}

/**
 * What deprecating a model would cost, per (alias, provider, provider_model)
 * over a window ending YESTERDAY.
 *
 * `enabled` is here for the same reason useMarkupPlans grew it: the Guard on
 * the Admin API writes every refusal to the audit trail, so a screen that
 * fires a read the operator may not make fills that trail with denials nobody
 * chose to attempt. The models screen passes `providers:read`.
 *
 * No `placeholderData`: the filter is fixed on the screen that reads it, so
 * there is no second key for a previous answer to come from.
 */
export function useModelUsage(
  filter: ModelUsageFilter = {},
  enabled = true,
): UseQueryResult<ModelUsageReport> {
  return useQuery({
    queryKey: adminKeys.modelUsage(filter),
    queryFn: () => adminClient.modelUsage(filter),
    enabled,
  });
}

// --------------------------------------------------------------------------
// Routing profiles
// --------------------------------------------------------------------------

/**
 * `enabled` so a screen can decline to fire the read at all.
 *
 * It defaults to true, so every existing call site is unchanged. The reason it
 * exists is the one UsageCard already records: the Admin API's Guard writes
 * every refusal to the audit trail, so a query issued for a permission the
 * operator does not hold puts a denial in that trail which nobody chose to
 * attempt.
 */
export function useRoutingProfiles(enabled = true): UseQueryResult<{
  routing_profiles: RoutingProfileSummary[];
}> {
  return useQuery({
    queryKey: adminKeys.routingProfiles,
    queryFn: () => adminClient.listRoutingProfiles(),
    enabled,
  });
}

export function useRoutingProfile(
  profileID: string,
  enabled = true,
): UseQueryResult<RoutingProfileDetail> {
  return useQuery({
    queryKey: adminKeys.routingProfile(profileID),
    queryFn: () => adminClient.getRoutingProfile(profileID),
    // A blank id is never fetched: a screen that has not found its profile yet
    // would otherwise ask for `/routing-profiles/` and get a 404 it has to
    // render as an error.
    enabled: enabled && profileID !== "",
  });
}

/**
 * Every named profile's detail at once, on the SAME keys useRoutingProfile
 * uses so the two share one cache — an operator who has just been on a
 * profile's page pays for nothing here.
 *
 * It exists because the model policies are the only place an ALIAS is written
 * down: there is no route that lists the alias vocabulary, and an alias is not
 * unique anywhere in the database (see 0027's header), so the deprecation
 * panel's replacement picker has to read the policies to know what it may
 * offer. That is one request per profile, which is why nothing fetches this
 * until that panel is actually open.
 */
export function useRoutingProfileDetails(
  profileIDs: string[],
): UseQueryResult<RoutingProfileDetail>[] {
  return useQueries({
    queries: profileIDs.map((profileID) => ({
      queryKey: adminKeys.routingProfile(profileID),
      queryFn: () => adminClient.getRoutingProfile(profileID),
    })),
  });
}

export interface RoutingProfileStatusVariables {
  profileID: string;
  status: EnabledStatus;
  reason: Reason;
}

export interface ReorderProvidersVariables {
  profileID: string;
  providerAccountIDs: string[];
  reason: Reason;
}

/**
 * Reorder a routing profile's provider chain.
 *
 * Invalidates the profile as well as the list, because the reorder changes the
 * model policies too and a cached detail would show the old fallback order
 * beside the new chain — which is the one combination that would make an
 * operator distrust both.
 */
export function useReorderRoutingProfileProviders(): UseMutationResult<
  RoutingProfileDetail,
  unknown,
  ReorderProvidersVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: ReorderProvidersVariables) =>
      adminClient.reorderRoutingProfileProviders(vars.profileID, vars.providerAccountIDs, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.routingProfile(vars.profileID) });
      await queries.invalidateQueries({ queryKey: adminKeys.routingProfiles });
    },
  });
}

export interface PackageStatusVariables {
  packageID: string;
  status: "draft" | "active";
  reason: Reason;
}

/**
 * Publish a package, or take it back to draft.
 *
 * Both keys, like every other package write: the list row carries the status
 * this changes, and a console showing "draft" on the list beside "active" on
 * the page an operator has just published from is the one combination that
 * makes them distrust both.
 */
export function useSetPackageStatus(): UseMutationResult<
  { package: Package; previous: string; changed: boolean },
  unknown,
  PackageStatusVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: PackageStatusVariables) =>
      adminClient.setPackageStatus(vars.packageID, vars.status, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.package(vars.packageID) });
      await queries.invalidateQueries({ queryKey: adminKeys.packages });
    },
  });
}

export interface AttachProviderVariables {
  profileID: string;
  body: NewProfileProvider;
  reason: Reason;
}

export interface ProfileProviderVariables {
  profileID: string;
  accountID: string;
  reason: Reason;
}

export interface ProfileProviderStatusVariables extends ProfileProviderVariables {
  enabled: boolean;
}

/**
 * The three writes that decide a profile's chain MEMBERSHIP, as against the
 * reorder above which decides its order.
 *
 * All three invalidate the same three keys, because all three change the same
 * three answers: the profile being edited, the routing list (whose rows count
 * the chain), and the provider pool — the account detail page lists "routing
 * profiles using this account", which is this table read from the other end,
 * and an attach that left it cached would have the two pages disagreeing about
 * a row they both draw from.
 */
function useProfileProviderInvalidation(): (profileID: string) => Promise<void> {
  const queries = useQueryClient();
  return async (profileID: string) => {
    await queries.invalidateQueries({ queryKey: adminKeys.routingProfile(profileID) });
    await queries.invalidateQueries({ queryKey: adminKeys.routingProfiles });
    await queries.invalidateQueries({ queryKey: adminKeys.providers });
  };
}

/**
 * Put one of the tenant's provider accounts into a profile's chain.
 *
 * The server appends and answers with the whole profile, so nothing here
 * computes a rank: the reply is the order the resolver will actually take.
 */
export function useAttachRoutingProfileProvider(): UseMutationResult<
  RoutingProfileDetail,
  unknown,
  AttachProviderVariables
> {
  const invalidate = useProfileProviderInvalidation();
  return useMutation({
    mutationFn: (vars: AttachProviderVariables) =>
      adminClient.attachRoutingProfileProvider(vars.profileID, vars.body, vars.reason),
    onSuccess: (_result, vars) => invalidate(vars.profileID),
  });
}

/** Disable or re-enable one entry without losing its rank. */
export function useSetRoutingProfileProviderStatus(): UseMutationResult<
  RoutingProfileDetail,
  unknown,
  ProfileProviderStatusVariables
> {
  const invalidate = useProfileProviderInvalidation();
  return useMutation({
    mutationFn: (vars: ProfileProviderStatusVariables) =>
      adminClient.setRoutingProfileProviderStatus(
        vars.profileID,
        vars.accountID,
        vars.enabled,
        vars.reason,
      ),
    onSuccess: (_result, vars) => invalidate(vars.profileID),
  });
}

/** Withdraw the authorization. The account stays in the tenant's pool. */
export function useDetachRoutingProfileProvider(): UseMutationResult<
  RoutingProfileDetail,
  unknown,
  ProfileProviderVariables
> {
  const invalidate = useProfileProviderInvalidation();
  return useMutation({
    mutationFn: (vars: ProfileProviderVariables) =>
      adminClient.detachRoutingProfileProvider(vars.profileID, vars.accountID, vars.reason),
    onSuccess: (_result, vars) => invalidate(vars.profileID),
  });
}

export function useSetRoutingProfileStatus(): UseMutationResult<
  RoutingProfileStatusResult,
  unknown,
  RoutingProfileStatusVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: RoutingProfileStatusVariables) =>
      adminClient.setRoutingProfileStatus(vars.profileID, vars.status, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.routingProfiles });
    },
  });
}

export interface NewRoutingProfileVariables {
  body: NewRoutingProfile;
  reason: Reason;
}

/**
 * Create a routing profile.
 *
 * Only the LIST is invalidated: the profile that comes back is one nothing
 * has a cache entry for yet, and the screen navigates to it rather than
 * seeding one — the detail query then fetches the profile as the server
 * holds it, which is the same rule useCreateProviderModel follows and for the
 * same reason. The server decides the id, the status (a profile is born
 * disabled) and the created_at.
 */
export function useCreateRoutingProfile(): UseMutationResult<
  RoutingProfileDetail,
  unknown,
  NewRoutingProfileVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: NewRoutingProfileVariables) =>
      adminClient.createRoutingProfile(vars.body, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.routingProfiles });
    },
  });
}

export interface RoutingProfileEditVariables {
  profileID: string;
  body: RoutingProfileEdit;
  reason: Reason;
}

/**
 * Edit a profile's own fields.
 *
 * Both keys, like the reorder above: the list row carries the name, the scope
 * and the markup plan this edit can move, so refreshing only the page the
 * operator is looking at would leave the routing list describing a profile
 * that has changed underneath it.
 */
export function useUpdateRoutingProfile(): UseMutationResult<
  RoutingProfileDetail,
  unknown,
  RoutingProfileEditVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: RoutingProfileEditVariables) =>
      adminClient.updateRoutingProfile(vars.profileID, vars.body, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.routingProfile(vars.profileID) });
      await queries.invalidateQueries({ queryKey: adminKeys.routingProfiles });
    },
  });
}

export interface NewModelPolicyVariables {
  profileID: string;
  body: NewModelPolicy;
  reason: Reason;
}

/**
 * Add a route to a profile.
 *
 * The list is invalidated as well as the profile because the routing list
 * shows a policy COUNT per profile, and a count that did not move after an
 * operator added a policy reads as a write that silently failed.
 */
export function useCreateModelPolicy(): UseMutationResult<
  { model_policy: ModelPolicy },
  unknown,
  NewModelPolicyVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: NewModelPolicyVariables) =>
      adminClient.createModelPolicy(vars.profileID, vars.body, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.routingProfile(vars.profileID) });
      await queries.invalidateQueries({ queryKey: adminKeys.routingProfiles });
    },
  });
}

export interface ModelPolicyEditVariables {
  profileID: string;
  policyID: string;
  body: ModelPolicyEdit;
  reason: Reason;
}

/** Re-rank a route or change what it may carry. A priority move renumbers
 *  the alias's other policies, so the whole profile is refetched rather than
 *  the one row merged. */
export function useUpdateModelPolicy(): UseMutationResult<
  { model_policy: ModelPolicy },
  unknown,
  ModelPolicyEditVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: ModelPolicyEditVariables) =>
      adminClient.updateModelPolicy(vars.profileID, vars.policyID, vars.body, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.routingProfile(vars.profileID) });
    },
  });
}

export interface ModelPolicyStatusVariables {
  profileID: string;
  policyID: string;
  status: EnabledStatus;
  reason: Reason;
}

/** Archive a route, or restore it. 'disabled' clears `allowed` and the row
 *  stays — there is no verb in this realm that removes a model policy, so
 *  the count on the list row does not move and only the profile is
 *  invalidated. */
export function useSetModelPolicyStatus(): UseMutationResult<
  { model_policy: ModelPolicy },
  unknown,
  ModelPolicyStatusVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: ModelPolicyStatusVariables) =>
      adminClient.setModelPolicyStatus(vars.profileID, vars.policyID, vars.status, vars.reason),
    onSuccess: async (_result, vars) => {
      await queries.invalidateQueries({ queryKey: adminKeys.routingProfile(vars.profileID) });
    },
  });
}

// --------------------------------------------------------------------------
// Pricing
// --------------------------------------------------------------------------

export function usePriceVersions(
  filter: PriceVersionFilter,
): UseQueryResult<{ price_versions: PriceVersion[] }> {
  return useQuery({
    queryKey: adminKeys.priceVersions(filter),
    queryFn: () => adminClient.listPriceVersions(filter),
  });
}

/**
 * The markup plans, optionally not asked for at all.
 *
 * `enabled` exists because this list is guarded by `pricing:read` and the
 * routing screens — which need a plan's CURRENCY to render a spend ceiling in
 * currency units — are guarded by `providers:read`. A security_admin holds the
 * second and not the first, so firing this query for them would earn a 403 and
 * a denial in the audit trail for a request nobody made: the Guard middleware
 * records every refusal, and an audit trail full of denials the console
 * generated on its own is one an operator learns to skim past.
 */
export function useMarkupPlans(
  filter: MarkupPlanFilter,
  options: { enabled?: boolean } = {},
): UseQueryResult<{ markup_plans: MarkupPlan[] }> {
  return useQuery({
    queryKey: adminKeys.markupPlans(filter),
    queryFn: () => adminClient.listMarkupPlans(filter),
    enabled: options.enabled ?? true,
  });
}

export interface NewPriceVersionVariables {
  body: NewPriceVersion;
  reason: Reason;
}

/**
 * Publishing prices invalidates the whole pricing prefix, not the one filter
 * that happens to be on screen.
 *
 * A new version closes the previous one, so every cached list that contained
 * the old row — under any provider or model filter the operator has looked at
 * this session — is now describing a version that has stopped applying.
 */
export function useCreatePriceVersion(): UseMutationResult<
  { price_version: PriceVersion },
  unknown,
  NewPriceVersionVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: NewPriceVersionVariables) =>
      adminClient.createPriceVersion(vars.body, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.pricing });
    },
  });
}

export interface NewMarkupPlanVariables {
  body: NewMarkupPlan;
  reason: Reason;
}

export function useCreateMarkupPlan(): UseMutationResult<
  { markup_plan: MarkupPlan },
  unknown,
  NewMarkupPlanVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: NewMarkupPlanVariables) =>
      adminClient.createMarkupPlan(vars.body, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.pricing });
    },
  });
}

/**
 * The review queue.
 *
 * A plain read — nothing here can change a quote, because the resolver never
 * looks at provider_price_proposals. The filter is in the key so "flagged
 * only" and "everything" do not share a cache entry.
 */
export function usePriceProposals(
  filter: PriceProposalFilter,
): UseQueryResult<{ price_proposals: PriceProposal[] }> {
  return useQuery({
    queryKey: adminKeys.priceProposals(filter),
    queryFn: () => adminClient.listPriceProposals(filter),
  });
}

export interface PublishPriceProposalVariables {
  proposalID: string;
  body: PublishPriceProposal;
  reason: Reason;
}

/**
 * Publishing invalidates the whole `pricing` prefix rather than the queue.
 *
 * One call writes in two places: the proposal leaves `proposed`, and a new
 * price version closes the previous one for that pair. A console that
 * refreshed only the queue would keep showing the superseded rate as the one
 * in force, which is the single number this screen exists to get right.
 */
export function usePublishPriceProposal(): UseMutationResult<
  PriceProposalPublished,
  unknown,
  PublishPriceProposalVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: PublishPriceProposalVariables) =>
      adminClient.publishPriceProposal(vars.proposalID, vars.body, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.pricing });
    },
  });
}

export interface RejectPriceProposalVariables {
  proposalID: string;
  reason: Reason;
}

/** Rejecting moves no money and appends no version, but it shares the prefix
 *  with publish so the two buttons on one row behave the same way. */
export function useRejectPriceProposal(): UseMutationResult<
  { price_proposal: PriceProposal },
  unknown,
  RejectPriceProposalVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: RejectPriceProposalVariables) =>
      adminClient.rejectPriceProposal(vars.proposalID, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.pricing });
    },
  });
}

export interface PriceVersionStatusVariables {
  versionID: string;
  status: PriceVersionTransition;
  reason: Reason;
}

/**
 * Close a published price version.
 *
 * The whole pricing prefix again, and for a stronger reason than the create:
 * closing a version changes what the gateway can price, and the row the
 * operator is looking at is not the only cached list it appears in. Nothing is
 * merged back from the response either — `changed: false` means the row was
 * already in that state, and a merge would paint a no-op as a write.
 */
export function useSetPriceVersionStatus(): UseMutationResult<
  PriceVersionStatusChange,
  unknown,
  PriceVersionStatusVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: PriceVersionStatusVariables) =>
      adminClient.setPriceVersionStatus(vars.versionID, vars.status, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.pricing });
    },
  });
}

export interface UpdateMarkupPlanVariables {
  planID: string;
  body: MarkupPlanEdit;
  reason: Reason;
}

/**
 * Correct a markup plan, which APPENDS a version and closes the current one.
 *
 * Two prefixes, because this write reaches a resource the caller did not name:
 * `routing.routing_profiles.markup_plan_id` pins a plan VERSION, so the store
 * re-points every profile onto the successor in the same transaction. A
 * routing profile page left un-invalidated would go on showing the plan id and
 * name of a version that has just been closed.
 */
export function useUpdateMarkupPlan(): UseMutationResult<
  MarkupPlanChange,
  unknown,
  UpdateMarkupPlanVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: UpdateMarkupPlanVariables) =>
      adminClient.updateMarkupPlan(vars.planID, vars.body, vars.reason),
    onSuccess: async () => {
      await Promise.all([
        queries.invalidateQueries({ queryKey: adminKeys.pricing }),
        queries.invalidateQueries({ queryKey: adminKeys.routingProfiles }),
      ]);
    },
  });
}

export interface ArchiveMarkupPlanVariables {
  planID: string;
  reason: Reason;
}

/** Withdrawing a plan needs no routing invalidation: the route refuses while
 *  any ACTIVE routing profile still prices against it, so a success is proof
 *  that no profile page on screen is describing this plan. */
export function useArchiveMarkupPlan(): UseMutationResult<
  MarkupPlanStatusChange,
  unknown,
  ArchiveMarkupPlanVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: ArchiveMarkupPlanVariables) =>
      adminClient.archiveMarkupPlan(vars.planID, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.pricing });
    },
  });
}

// --------------------------------------------------------------------------
// Subscription packages
// --------------------------------------------------------------------------

/**
 * usePackages is the catalogue as an operator filters it.
 *
 * The filter is in the query KEY as well as in the request — "every package"
 * and "App A's active packages" are different questions, and one cache entry
 * between them shows whichever answer landed last under whichever filter is
 * on screen.
 */
export function usePackages(filter: PackageFilter): UseQueryResult<{ packages: Package[] }> {
  return useQuery({
    queryKey: adminKeys.packageList(filter),
    queryFn: () => adminClient.listPackages(filter),
  });
}

/**
 * One package, its version history and who is on it.
 *
 * The subscriber count on this read decides whether the package can be
 * withdrawn, so it is not cached longer than any other admin read: an
 * operator looking at "0 subscribers" from five minutes ago is an operator
 * about to be refused by the server, which is the correct outcome but a
 * confusing one.
 */
export function usePackage(packageID: string): UseQueryResult<PackageDetail> {
  return useQuery({
    queryKey: adminKeys.package(packageID),
    queryFn: () => adminClient.getPackage(packageID),
  });
}

export interface NewPackageVariables {
  body: NewPackage;
  reason: Reason;
}

/**
 * Defining a package invalidates the whole `packages` prefix.
 *
 * Not just the filter on screen: the new row belongs in "every package", in
 * its application's list and in the draft list, and an operator who filtered
 * to that application a minute ago would otherwise come back to a cached list
 * that does not contain the package they just created.
 */
export function useCreatePackage(): UseMutationResult<
  { package: Package },
  unknown,
  NewPackageVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: NewPackageVariables) => adminClient.createPackage(vars.body, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.packages });
    },
  });
}

export interface AppendPackageVersionVariables {
  packageID: string;
  body: NewPackageVersion;
  reason: Reason;
}

/**
 * A re-price changes the package's row in the LIST as well as the page the
 * operator is on — `current_version` is what the list renders — so this
 * invalidates the shared prefix rather than the one detail key.
 */
export function useAppendPackageVersion(): UseMutationResult<
  PackageVersionChange,
  unknown,
  AppendPackageVersionVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: AppendPackageVersionVariables) =>
      adminClient.appendPackageVersion(vars.packageID, vars.body, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.packages });
    },
  });
}

export interface MigrateSubscribersVariables {
  packageID: string;
  targetPackageID: string;
  reason: Reason;
}

/**
 * Moving subscribers changes the count on BOTH packages, and the source's new
 * count is what decides whether the withdraw button beside it is live — so
 * this invalidates the whole `packages` prefix and the archive guard re-arms
 * itself without anybody reloading the page.
 *
 * Invalidating the two detail keys by hand would be narrower and wrong: the
 * catalogue LIST renders `subscriber_count` too, and an operator who came
 * from it would go back to a screen still claiming somebody is on the package
 * they just emptied.
 */
export function useMigratePackageSubscribers(): UseMutationResult<
  PackageMigrationReport,
  unknown,
  MigrateSubscribersVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: MigrateSubscribersVariables) =>
      adminClient.migratePackageSubscribers(vars.packageID, vars.targetPackageID, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.packages });
      // And the users prefix, which is where a customer's own subscription
      // list is cached: a migration changes which package every moved
      // subscriber is on, and the operator who then opens one of those
      // accounts must not be shown the package they were moved off.
      await queries.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
}

export interface ArchivePackageVariables {
  packageID: string;
  reason: Reason;
}

/**
 * Withdrawing a package takes it out of the customer's plan list, so the
 * catalogue's status filter, the detail page and any list already fetched are
 * all now describing an offer that is no longer on sale. Same prefix, same
 * reason.
 */
export function useArchivePackage(): UseMutationResult<
  PackageArchive,
  unknown,
  ArchivePackageVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: ArchivePackageVariables) =>
      adminClient.archivePackage(vars.packageID, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.packages });
    },
  });
}

// --------------------------------------------------------------------------
// Money
// --------------------------------------------------------------------------

/**
 * The money hooks are all reads, and there is no mutation below them.
 *
 * That is not an omission: the Admin API exposes no write on this surface,
 * because the ledger is append-only in the database and a correction is a
 * compensating transaction the platform posts, not an edit a console makes.
 * There is consequently nothing here to invalidate — the money views go stale
 * because the platform moved, never because this portal did — so they refetch
 * on focus like every other read rather than being pushed at.
 */
export function useWallets(filter: WalletFilter): UseQueryResult<{ wallets: Wallet[] }> {
  return useQuery({
    queryKey: adminKeys.walletList(filter),
    queryFn: () => adminClient.listWallets(filter),
  });
}

export function useWallet(walletID: string): UseQueryResult<WalletDetail> {
  return useQuery({
    queryKey: adminKeys.wallet(walletID),
    queryFn: () => adminClient.getWallet(walletID),
  });
}

/**
 * The ledger is a SEPARATE query from the wallet, not a field on it.
 *
 * It is behind a different permission (`ledger:read`), so an operator holding
 * only `wallets:read` gets a wallet page that renders with a 403 where the
 * ledger would be — rather than a wallet page that fails to load at all
 * because one card they may not see took the whole screen down with it.
 */
export function useWalletLedger(
  walletID: string,
  limit?: number,
): UseQueryResult<{ transactions: LedgerTransaction[] }> {
  return useQuery({
    queryKey: adminKeys.walletLedger(walletID, limit),
    queryFn: () => adminClient.getWalletLedger(walletID, limit),
  });
}

export function useReservations(
  filter: ReservationFilter,
): UseQueryResult<{ reservations: Reservation[] }> {
  return useQuery({
    queryKey: adminKeys.reservations(filter),
    queryFn: () => adminClient.listReservations(filter),
  });
}

export function useTopUps(filter: TopUpFilter): UseQueryResult<{ top_ups: TopUp[] }> {
  return useQuery({
    queryKey: adminKeys.topUps(filter),
    queryFn: () => adminClient.listTopUps(filter),
  });
}

export function useSettlementFailures(
  limit?: number,
): UseQueryResult<{ failures: SettlementFailure[] }> {
  return useQuery({
    queryKey: adminKeys.settlementFailures(limit),
    queryFn: () => adminClient.listSettlementFailures(limit),
  });
}

// --------------------------------------------------------------------------
// Analytics
// --------------------------------------------------------------------------

/**
 * The platform dashboard, in one request.
 *
 * `enabled` exists for the same reason `useModelUsage`'s does: the Admin API's
 * Guard writes every refusal to the audit trail, so firing this for an
 * operator who does not hold `analytics:read` earns a 403 AND an audit denial
 * for a request nobody chose to make. The screen decides not to ask.
 *
 * No invalidation anywhere below it. The figures come from the worker's
 * nightly rollups and nothing this console does moves them, so the dashboard
 * goes stale because the platform moved rather than because the portal did.
 */
export function useAnalyticsOverview(
  query: AnalyticsWindowQuery,
  enabled = true,
): UseQueryResult<AnalyticsOverview> {
  return useQuery({
    queryKey: adminKeys.analyticsOverview(query),
    queryFn: () => adminClient.analyticsOverview(query),
    enabled,
  });
}

/**
 * The leaderboard behind the dashboard's headline: who is doing all of this.
 *
 * The ordering and the page size go to the SERVER rather than being applied to
 * the answer, and that is the whole reason this hook takes them. The route
 * ranks and then cuts at `limit`, so re-ordering the rows it returned would
 * answer "the heaviest ten accounts by tokens, arranged by revenue" under a
 * heading reading "the top ten by revenue" — a different set of people. The
 * three money orderings are refused with a 403 without `wallets:read`, which
 * is another thing only the server can decide.
 *
 * `placeholderData` keeps the table on screen while the next ordering is
 * fetched. Both the sort and the page size are in the key, so without it every
 * click on a heading would replace the rows with a spinner and lose the
 * operator's place — and the figures that arrive are the same figures in a
 * different order, so there is nothing misleading about the old ones being
 * visible for a moment. The card marks itself busy while it refetches.
 */
export function useAnalyticsTopUsers(
  query: AnalyticsTopUsersQuery,
  enabled = true,
): UseQueryResult<AnalyticsTopUsers> {
  return useQuery({
    queryKey: adminKeys.analyticsUsers(query),
    queryFn: () => adminClient.analyticsUsers(query),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/**
 * One account's usage, for the card on that account's page.
 *
 * `enabled` carries the same argument `useAnalyticsOverview`'s does, with one
 * addition: this route is guarded TWICE, on `analytics:read` and on
 * `users:read`. The page it sits on already needed the second to render at
 * all, so the card asks about the first — and an operator without it gets no
 * card and no denial in the audit trail, rather than an empty card and a 403.
 *
 * No `placeholderData`. Unlike the leaderboard, the only thing in this key
 * that an operator changes is the WINDOW, and the figures for one window are
 * not a reordering of another's — leaving September's totals on screen under
 * an August heading is the one failure a windowed card must not have.
 */
export function useAnalyticsUserDetail(
  userID: string,
  query: AnalyticsWindowQuery,
  enabled = true,
): UseQueryResult<AnalyticsUserDetail> {
  return useQuery({
    queryKey: adminKeys.analyticsUserDetail(userID, query),
    queryFn: () => adminClient.analyticsUserDetail(userID, query),
    enabled,
  });
}

/**
 * One application's usage and revenue, for the card on that application's page.
 *
 * `enabled` carries the same argument `useAnalyticsOverview`'s does, and here
 * it is the WHOLE gate: this is the one analytics route guarded on
 * `analytics:read` alone — the finest grain in the document is an application
 * and a day, so there is nobody in it to need `users:read` for. The money
 * inside still needs `wallets:read` and comes back ABSENT rather than zeroed
 * without it, which is a decision only the server makes.
 *
 * No `placeholderData`, on the rule `useAnalyticsUserDetail` sets: the only
 * thing in this key an operator changes is the window, and one window's
 * figures are not a reordering of another's. Leaving September's totals on
 * screen under an August heading is the failure a windowed card must not have.
 */
export function useAnalyticsApplications(
  query: AnalyticsApplicationsQuery,
  enabled = true,
): UseQueryResult<AnalyticsApplications> {
  return useQuery({
    queryKey: adminKeys.analyticsApplications(query),
    queryFn: () => adminClient.analyticsApplications(query),
    enabled,
  });
}

/**
 * The money picture over the window, per currency, for `/analytics/revenue`.
 *
 * `enabled` carries the same argument `useAnalyticsOverview`'s does with one
 * addition: this route is guarded TWICE, on `analytics:read` and on
 * `wallets:read`, and the second gate is at the DOOR rather than over the
 * fields. Every figure in the document is money, so there is no shape left to
 * return with the money withheld — the server answers 403 and audits the
 * refusal, and the screen therefore asks for neither the answer nor the denial
 * unless the operator holds both grants.
 *
 * No `placeholderData`, on the rule `useAnalyticsUserDetail` sets: the only
 * thing in this key an operator changes is the window, and one window's
 * figures are not a reordering of another's. Leaving September's revenue on
 * screen under an August heading is the failure a windowed screen must not
 * have — and on this screen it would be a wrong figure quoted about money.
 */
export function useAnalyticsRevenue(
  query: AnalyticsWindowQuery,
  enabled = true,
): UseQueryResult<AnalyticsRevenue> {
  return useQuery({
    queryKey: adminKeys.analyticsRevenue(query),
    queryFn: () => adminClient.analyticsRevenue(query),
    enabled,
  });
}

/**
 * Where the provider's own accounting disagrees with ours.
 *
 * `enabled` is the operator's own `analytics:read`, on `useAlerts`' rule: the
 * Admin API's Guard writes every refusal to the audit trail, so firing this for
 * an operator who does not hold the grant earns a 403 AND an audited denial for
 * a request nobody chose to make.
 *
 * No `placeholderData` and no invalidation anywhere. A verdict changes when the
 * WORKER re-runs a day against a re-fetched provider report; nothing in this
 * console can move one, because nothing in this realm can write to schema
 * `recon` at all.
 */
export function useAnalyticsDrift(
  query: UsageDriftQuery,
  enabled = true,
): UseQueryResult<UsageDriftReport> {
  return useQuery({
    queryKey: adminKeys.analyticsDrift(query),
    queryFn: () => adminClient.analyticsDrift(query),
    enabled,
  });
}

// --------------------------------------------------------------------------
// The audit trail
// --------------------------------------------------------------------------

export function useAuditActions(filter: AuditFilter): UseQueryResult<{ actions: AuditAction[] }> {
  return useQuery({
    queryKey: adminKeys.audit(filter),
    queryFn: () => adminClient.listAuditActions(filter),
  });
}

// --------------------------------------------------------------------------
// The system failure log
// --------------------------------------------------------------------------

/**
 * One filtered page of faults, newest FIRST OCCURRENCE first.
 *
 * No `placeholderData`, and that is the opposite choice from the leaderboard's.
 * There, changing the sort reorders one set and keeping the previous page on
 * screen avoids a blink. Here, changing the filter changes WHICH FAULTS are
 * being asked about — leaving the worker's rows up while `?service=edge` is in
 * flight is the failure this whole screen is built to avoid, a list of rows
 * under a heading that does not describe them. The state machine in
 * `screens/failures.ts` has a loading render for exactly this moment.
 *
 * `staleTime` because the nav badge mounts this query on EVERY screen: a
 * refetch per navigation would be a database read per click for a number that
 * moves on the platform's schedule rather than the operator's. Acknowledging
 * invalidates the `failures` prefix, so an act by this operator is never what
 * the stale window hides.
 */
export function useFailures(query: FailureQuery, enabled = true): UseQueryResult<FailurePage> {
  return useQuery({
    queryKey: adminKeys.failureList(query),
    queryFn: () => adminClient.listFailures(query),
    staleTime: 30_000,
    enabled,
  });
}

/**
 * The OPEN set, unfiltered — what the nav badge counts.
 *
 * The same hook and therefore the same cache entry the screen uses when it is
 * unfiltered, which is the common case: an operator following the badge gets
 * the page it was counting rather than a second read of the same rows.
 *
 * `enabled` is the operator's own `failures:read`. Without it the query never
 * runs, and a query that never ran is `isPending` for ever — which is why the
 * badge is driven by `navBadge`, whose loading render is *nothing at all*
 * rather than a spinner.
 */
export function useOpenFailures(enabled = true): UseQueryResult<FailurePage> {
  return useFailures({}, enabled);
}

/**
 * One fault whole: the scrubbed context, the resolved subjects, and the
 * acknowledgement.
 *
 * Fetched per EXPANDED ROW and not with the list, because 0039 bounds `context`
 * at 8 KiB and a fifty-row page carrying it would be four hundred kilobytes of
 * detail an operator reads one row of. The route writes no audit row — unlike
 * the app-metadata reads, whose consent is the disclosure — so opening a row
 * costs nothing but the read.
 */
export function useFailure(failureID: string, enabled = true): UseQueryResult<FailureDetail> {
  return useQuery({
    queryKey: adminKeys.failure(failureID),
    queryFn: () => adminClient.getFailure(failureID),
    enabled,
  });
}

/**
 * The same filtered set counted four ways — the charts on `/failures`.
 *
 * The SAME query object the list is given, so the charts and the table under
 * them are two views of one set. The one thing that differs is the window: the
 * summary route DEFAULTS it to thirty days because a daily series has to name
 * the days it is counting, where the list has no default window at all. Both
 * responses report the window they resolved, and the screen compares them —
 * `sameWindow` — rather than assuming they agree.
 *
 * The same `staleTime` as the list, and for the same reason: the two are
 * refetched together by an acknowledgement invalidating the `failures` prefix,
 * and a chart that expired on a different schedule from the table beneath it
 * would draw one answer over another.
 */
export function useFailureSummary(
  query: FailureQuery,
  enabled = true,
): UseQueryResult<FailureSummary> {
  return useQuery({
    queryKey: adminKeys.failureSummary(query),
    queryFn: () => adminClient.failureSummary(query),
    staleTime: 30_000,
    enabled,
  });
}

export interface AcknowledgeSelectionVariables {
  failureIDs: string[];
  reason: Reason;
}

export interface AcknowledgeFilterVariables {
  query: FailureQuery;
  reason: Reason;
}

/**
 * Clear a SELECTION: the rows an operator ticked.
 *
 * Its own hook rather than a flag on the one below, on the rule the three
 * device revocations set: they are two different asks, the server refuses a
 * body carrying both, and a shared mutation with a boolean is one mis-set
 * boolean away from clearing a set nobody looked at.
 *
 * Invalidating the whole `failures` PREFIX and not one entry. An
 * acknowledgement changes three things at once — the table the operator is
 * looking at, the nav badge counting the open set, and the summary the charts
 * are drawn from — and they are three cache entries under one prefix. Awaited,
 * so the dialog's busy state covers the refetch and the operator does not land
 * back on the page they just changed.
 */
export function useAcknowledgeFailures(): UseMutationResult<
  FailureAcknowledgement,
  unknown,
  AcknowledgeSelectionVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: AcknowledgeSelectionVariables) =>
      adminClient.acknowledgeFailures(vars.failureIDs, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.failures });
    },
  });
}

/**
 * Clear everything a FILTER matches.
 *
 * The query handed in is the one the table is drawn from, so "clear what I am
 * looking at" is structural rather than two callers agreeing — the client
 * renders it through the same builder both reads use and drops `limit`, which
 * is a page size and does not bound an act.
 *
 * This is the form the server can REFUSE: a filter matching more than
 * `max_matched` comes back 422 with the count on it, and nothing is written.
 * The screen catches that and does not rethrow — see `runClear` in
 * screens/failures.tsx.
 */
export function useAcknowledgeMatchingFailures(): UseMutationResult<
  FailureAcknowledgement,
  unknown,
  AcknowledgeFilterVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: AcknowledgeFilterVariables) =>
      adminClient.acknowledgeMatchingFailures(vars.query, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.failures });
    },
  });
}

// --------------------------------------------------------------------------
// Alerting
// --------------------------------------------------------------------------

/**
 * What has fired.
 *
 * `enabled` is the operator's own `wallets:read`, on `useModelUsage`'s rule:
 * the Admin API's Guard writes every refusal to the audit trail, so firing
 * this for an operator who does not hold the grant earns a 403 AND an audited
 * denial for a request nobody chose to make. The screen decides not to ask.
 *
 * There is no invalidation anywhere below this hook except the threshold
 * mutation's, and that one deliberately does not reach here. Resolution and
 * acknowledgement are edges the ENGINE observes, so a fired alert changes
 * because the platform moved — never because this console did — and these
 * reads refetch on focus like the rest of the money surface.
 */
export function useAlerts(
  filter: AlertFilter,
  enabled = true,
): UseQueryResult<{ alerts: Alert[] }> {
  return useQuery({
    queryKey: adminKeys.alertList(filter),
    queryFn: () => adminClient.listAlerts(filter),
    enabled,
  });
}

/**
 * One crossing, with its deliveries — fetched per OPENED ROW rather than with
 * the list.
 *
 * The deliveries are a query per alert on the server's side, which is exactly
 * why the list route does not carry them: a fifty-row page would be two
 * hundred delivery rows an operator reads none of. Opening one costs a read
 * and writes no audit row.
 */
export function useAlert(alertID: string, enabled = true): UseQueryResult<AlertDetail> {
  return useQuery({
    queryKey: adminKeys.alert(alertID),
    queryFn: () => adminClient.getAlert(alertID),
    enabled,
  });
}

/**
 * One account's runout threshold, or the platform floor.
 *
 * A null `threshold` in the answer is a SUCCESS and the normal state of an
 * account nobody has tuned, so this hook's `isSuccess` says nothing about
 * whether an override exists — read the field. `enabled` exists for the same
 * reason `useAlerts`' does; the wallet page passes its own `wallets:read`.
 */
export function useAlertThreshold(
  accountID: string,
  kind: AlertKind = "wallet_runout",
  enabled = true,
): UseQueryResult<AlertThresholdEnvelope> {
  return useQuery({
    queryKey: adminKeys.alertThreshold(accountID, kind),
    queryFn: () => adminClient.getAlertThreshold(accountID, kind),
    enabled,
  });
}

export interface SetAlertThresholdVariables {
  accountID: string;
  body: AlertThresholdEdit;
  reason: Reason;
}

/**
 * Move the line one account is measured against.
 *
 * Invalidates the `alert-thresholds` PREFIX and nothing else — pointedly not
 * the `alerts` prefix beside it. The edit changes what the next sweep will
 * fire; it changes no crossing that has already happened, and refetching the
 * fired list here would suggest to an operator that the rows in front of them
 * had been re-measured against the number they just typed.
 *
 * Awaited, so the reason dialog's busy state covers the refetch and the
 * operator does not land back on a card still showing the old figure.
 */
export function useSetAlertThreshold(): UseMutationResult<
  AlertThresholdEnvelope,
  unknown,
  SetAlertThresholdVariables
> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (vars: SetAlertThresholdVariables) =>
      adminClient.setAlertThreshold(vars.accountID, vars.body, vars.reason),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: adminKeys.alertThresholds });
    },
  });
}
